import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {EMPTY, Observable, TimeoutError, of} from 'rxjs';
import {catchError, switchMap, timeout} from 'rxjs/operators';
import {AuthService} from '@auth0/auth0-angular';
import {LogoutService} from '../services/logout.service';
import {AUTH0_EXPIRED_ERRORS} from './auth0-expired-errors';

const TOKEN_TIMEOUT_MS = 15000;

/**
 * Remplace le token statique (TokenService._token, fige au bootstrap) par un token
 * frais du SDK Auth0 a chaque requete authentifiee. Sans ce refresh, le token expire
 * apres une periode d'inactivite et toutes les requetes partent en 401 (issue #4178).
 *
 * Le timeout borne getAccessTokenSilently : si Auth0 ne repond pas (silent renewal
 * qui hang), on force un logout propre au lieu de laisser l'UI figee.
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {

    constructor(
        private auth: AuthService,
        private logoutService: LogoutService,
    ) {
    }

    intercept<Body>(req: HttpRequest<Body>, next: HttpHandler): Observable<HttpEvent<Body>> {
        // Seules les requetes deja marquees authentifiees par les services (bo/ticketing)
        // sont concernees — ne jamais toucher les requetes du bootstrap ou du login.
        if (!req.headers.has('authorization')) {
            return next.handle(req);
        }

        return this.auth.getAccessTokenSilently().pipe(
            timeout(TOKEN_TIMEOUT_MS),
            catchError((err: {error?: string; message?: string}) => {
                console.error('Failed to refresh token:', err?.error || err?.message || err);
                if (err instanceof TimeoutError || AUTH0_EXPIRED_ERRORS.includes(err?.error ?? '')) {
                    this.logoutService.logout();
                    return EMPTY;
                }
                // Erreur inattendue : on laisse partir la requete avec le token existant,
                // l'AuthExpiredInterceptor gerera un eventuel 401.
                return of(null);
            }),
            switchMap((token) => {
                const authReq = token
                    ? req.clone({setHeaders: {authorization: `Bearer ${token}`}})
                    : req;
                return next.handle(authReq);
            }),
        );
    }
}
